// THE CLIENT NET LAYER — the one interface the renderer talks to (docs/NET.md).
//
//   const net = new TownNet({ endpoint: "wss://…/ws", join: { name, pid, cos, first } });
//   await net.start({ kind: "public" });        // or { kind: "code", code } / { kind: "new" }; ?solo=1 or no server → offline
//   each frame:  net.input({ mx, mz, sprint, act });  const v = net.view(performance.now());  for (const e of net.drainEvents()) …
//   Inspector:   net.pop(id, [x, y, z]) · net.mark(id, [x, y, z]) then net.pop(id) · net.whistle()
//
// Other bodies are drawn INTERP_MS behind the server, interpolated between snapshots. Your own body is PREDICTED: every
// local sim tick sends one input and moves the body with the same pure step the server uses (shared/sim predictStep);
// each snapshot says which input the server applied last, and the client replays the ones after it from the server's
// position (reconciliation), melting any small difference away over PRED_SMOOTH_MS.
import * as T from "../../../shared/tuning";
import { DT, predictStep } from "../../../shared/sim";
import { decodePublic, decodePrivate, type JoinOptions, type Snap, type InputMsg, type BodyState, type HeaderState, type MeState } from "../../../shared/protocol";
import { OnlineTransport } from "./online";
import { OfflineTransport } from "./offline";
import type { Transport, TransportHandlers } from "./transport";
import type { NetMode, Target, TownView, ViewBody, NetEvent, InputState, RosterRow, Cosmetics, Call } from "./types";

export type { NetMode, Target, TownView, ViewBody, NetEvent, InputState, RosterRow } from "./types";

type Frame = { t: number; head: HeaderState; bodies: Map<number, BodyState> };   // t = server seconds
const BUFFER = 24;                // snapshots kept for interpolation (~1.6 s)
const CONNECT_TIMEOUT_MS = 5000;

export class TownNet {
  mode: NetMode = "idle";
  seat = -1; code = ""; error = "";
  private tr: Transport | null = null;
  private frames: Frame[] = [];
  private head: HeaderState | null = null; private me: MeState | null = null; private roster: RosterRow[] = [];
  private events: NetEvent[] = [];
  private offset = -Infinity;       // server seconds − local seconds (the least-delayed sample wins, drifting slowly)
  // input + prediction
  private inp: InputState = { mx: 0, mz: 0, sprint: false, act: false }; private actLatch = false;
  private seq = 0; private acc = 0; private lastMs = -1;
  private pending: InputMsg[] = [];
  private pred: { x: number; z: number; h: number } | null = null;
  private err = { x: 0, z: 0 }; private errAt = 0;
  private lastSpeed = new Map<number, { x: number; z: number; t: number; v: number }>();

  constructor(private opts: { endpoint: string; join: JoinOptions; solo?: boolean }) {}

  // ---- lifecycle ---------------------------------------------------------------------------------------------------

  /** go online (public / code / new private table); falls back to offline when the server cannot be reached (or solo) */
  async start(target: Target = { kind: "public" }): Promise<NetMode> {
    if (this.opts.solo || (typeof location !== "undefined" && new URLSearchParams(location.search).get("solo") === "1")) { this.offline(); return this.mode; }
    const ok = await this.connect(target);
    if (!ok && target.kind === "public") this.offline();   // a code that does not exist is an answer, not an outage
    return this.mode;
  }
  /** online only; resolves false with `error` set when it could not join */
  async connect(target: Target): Promise<boolean> {
    this.reset(); this.mode = "connecting";
    const tr = new OnlineTransport(this.opts.endpoint, this.handlers());
    const timeout = new Promise<{ ok: boolean; error?: string }>((r) => setTimeout(() => r({ ok: false, error: "timed out" }), CONNECT_TIMEOUT_MS));
    const res = await Promise.race([tr.open(target, this.opts.join), timeout]);
    if (!res.ok) { tr.close(); this.mode = "idle"; this.error = res.error || "could not connect"; this.push({ k: "net", state: "error", detail: this.error }); return false; }
    this.tr = tr; this.mode = "online"; this.push({ k: "net", state: "online" });
    return true;
  }
  /** the shared sim + bots right here in the tab */
  offline(seed?: number) {
    this.reset();
    this.tr = new OfflineTransport(this.handlers(), this.opts.join, seed);
    this.mode = "offline"; this.push({ k: "net", state: "offline" });
  }
  leave() { this.tr?.close(); this.tr = null; this.mode = "idle"; }

  private reset() {
    this.tr?.close(); this.tr = null; this.frames = []; this.head = null; this.me = null; this.roster = []; this.offset = -Infinity;
    this.seq = 0; this.pending = []; this.pred = null; this.acc = 0; this.lastMs = -1; this.err = { x: 0, z: 0 }; this.seat = -1; this.code = ""; this.error = "";
  }
  private push(e: NetEvent) { this.events.push(e); }
  private handlers(): TransportHandlers {
    return {
      seat: (m) => { this.seat = m.seat; this.code = m.code; },
      snap: (s, recvMs) => this.onSnap(s, recvMs),
      events: (evs) => { for (const e of evs) this.events.push(e); },
      status: (state, detail) => {
        if (state === "online") this.mode = "online"; else if (state === "reconnecting") this.mode = "reconnecting";
        else if (state === "lost") { this.mode = "idle"; this.tr = null; }
        this.push({ k: "net", state, detail });
      },
    };
  }

  // ---- what the renderer sends -------------------------------------------------------------------------------------

  /** the controls as of this frame (sampled into one input per sim tick; a tap of ACTION shorter than a tick still counts) */
  input(i: InputState) { this.inp = { mx: i.mx, mz: i.mz, sprint: !!i.sprint, act: !!i.act }; if (i.act) this.actLatch = true; }
  /** POP: the body id under the cursor + the aim point (where the camera ray passed closest to that body's axis) */
  pop(id: number, aim?: [number, number, number]) { this.tr?.pop(aim ? { id, p: aim } : { id }); }
  /** touch: tap marks a body (validated with the tap's ray), the POP button then fires pop(id) with no aim */
  mark(id: number, aim: [number, number, number]) { this.tr?.mark({ id, p: aim }); }
  whistle(call?: Call) { this.tr?.whistle(call ? { c: call } : {}); }
  setCosmetics(c: Partial<Cosmetics>) { this.opts.join.cos = { ...this.opts.join.cos, ...c }; this.tr?.cos(c); }
  setName(n: string) { this.opts.join.name = n; this.tr?.name(n); }
  /** FX / sound / HUD events since the last call */
  drainEvents(): NetEvent[] { const e = this.events; this.events = []; return e; }

  // ---- the frame ---------------------------------------------------------------------------------------------------

  view(nowMs: number): TownView {
    this.tr?.pump(nowMs);
    this.tickInputs(nowMs);
    const empty: TownView = { mode: this.mode, ready: false, seat: this.seat, code: this.code, head: this.head, me: this.me, bodies: [], roster: this.roster, serverTime: 0 };
    if (!this.frames.length || !this.head) return empty;
    // interpolate everyone INTERP_MS behind our best estimate of the server clock
    const rt = Math.max(this.frames[0].t, Math.min(nowMs / 1000 + this.offset - T.INTERP_MS / 1000, this.frames[this.frames.length - 1].t));
    let i = this.frames.length - 1; while (i > 0 && this.frames[i - 1].t > rt) i--;
    const b = this.frames[i], a = this.frames[Math.max(0, i - 1)];
    const span = b.t - a.t, f = span > 1e-6 ? Math.max(0, Math.min(1, (rt - a.t) / span)) : 1;
    const meId = this.me?.me || 0;
    const out: ViewBody[] = [];
    for (const [id, nb] of b.bodies) {
      const ob = a.bodies.get(id);
      const x = ob ? ob.x + (nb.x - ob.x) * f : nb.x, z = ob ? ob.z + (nb.z - ob.z) * f : nb.z;
      const h = ob ? lerpAngle(ob.h, nb.h, f) : nb.h;
      const src = f < 0.5 && ob ? ob : nb;
      const vb: ViewBody = { ...src, x, z, h, me: id === meId, speed: 0, reveal: this.head.reveals.get(id) || null };
      if (vb.me && this.pred && this.me && !this.me.locked) {
        const k = Math.max(0, 1 - (nowMs - this.errAt) / T.PRED_SMOOTH_MS);
        vb.x = this.pred.x + this.err.x * k; vb.z = this.pred.z + this.err.z * k; vb.h = this.pred.h;
        vb.anim = this.ownAnim(src.anim);
      }
      vb.speed = this.speedOf(id, vb.x, vb.z, nowMs);
      out.push(vb);
    }
    return { mode: this.mode, ready: true, seat: this.seat, code: this.code, head: this.head, me: this.me, bodies: out, roster: this.roster, serverTime: rt };
  }

  /** one input per local sim tick: send it, remember it, and move our own body with the server's own step */
  private tickInputs(nowMs: number) {
    if (this.lastMs < 0) { this.lastMs = nowMs; return; }
    this.acc += Math.min(0.25, (nowMs - this.lastMs) / 1000); this.lastMs = nowMs;
    while (this.acc >= DT) {
      this.acc -= DT;
      if (!this.tr) continue;
      const m: InputMsg = { s: ++this.seq, mx: round3(this.inp.mx), mz: round3(this.inp.mz), sp: this.inp.sprint ? 1 : 0, a: this.inp.act || this.actLatch ? 1 : 0 };
      this.actLatch = false;
      this.tr.input(m);
      this.pending.push(m); if (this.pending.length > 120) this.pending.shift();
      if (this.pred && this.me && !this.me.locked && this.me.me) { const p = predictStep(this.pred.x, this.pred.z, this.pred.h, m.mx, m.mz, this.speedFor(m)); this.pred = p; }
    }
  }
  private speedFor(m: InputMsg) { return this.me?.role === "inspector" ? T.INSPECTOR_WALK : m.sp ? T.SPRINT : T.WALK; }
  /** our own anim follows our own stick (the server's arrives INTERP_MS late) */
  private ownAnim(server: BodyState["anim"]): BodyState["anim"] {
    if (server !== "idle" && server !== "walk" && server !== "run" && server !== "look_around") return server;
    const moving = Math.hypot(this.inp.mx, this.inp.mz) > T.MOVE_DEADZONE;
    return moving ? (this.me?.role === "sneak" && this.inp.sprint ? "run" : "walk") : server === "walk" || server === "run" ? "idle" : server;
  }
  private speedOf(id: number, x: number, z: number, nowMs: number) {
    const p = this.lastSpeed.get(id);
    if (!p) { this.lastSpeed.set(id, { x, z, t: nowMs, v: 0 }); return 0; }
    const dt = (nowMs - p.t) / 1000;
    if (dt > 0.004) { const v = Math.hypot(x - p.x, z - p.z) / dt; p.v = p.v * 0.6 + Math.min(10, v) * 0.4; p.x = x; p.z = z; p.t = nowMs; }
    return p.v;
  }

  private onSnap(s: Snap, recvMs: number) {
    const { head, bodies } = decodePublic(s.p);
    const t = head.tick * DT;
    // clock: the least-delayed snapshot sets the offset; it drifts down slowly so a one-off fast packet cannot pin it
    const sample = t - recvMs / 1000;
    this.offset = this.offset === -Infinity || sample > this.offset ? sample : this.offset - 0.0005;
    if (this.frames.length && t <= this.frames[this.frames.length - 1].t) { if (head.round !== this.frames[this.frames.length - 1].head.round) this.frames = []; else return; }
    if (this.head && head.round !== this.head.round) this.frames = [];   // a new round: everyone is new, nobody slides across
    this.frames.push({ t, head, bodies: new Map(bodies.map((b) => [b.id, b])) });
    if (this.frames.length > BUFFER) this.frames.shift();
    this.head = head;
    if (s.p.ro) this.roster = s.p.ro.map(([name, human, insp], seat) => ({ seat, name, human: !!human, inspector: !!insp }));
    const me = decodePrivate(s.m);
    this.me = me;
    // reconciliation: start from where the server says we are after input `ack`, replay what it has not seen yet
    this.pending = this.pending.filter((m) => m.s > me.ack);
    if (!me.me || me.locked) { this.pred = me.me ? { x: me.x, z: me.z, h: me.h } : null; return; }
    let p = { x: me.x, z: me.z, h: me.h };
    for (const m of this.pending) p = predictStep(p.x, p.z, p.h, m.mx, m.mz, this.speedFor(m));
    if (this.pred) {
      const dx = this.pred.x - p.x, dz = this.pred.z - p.z;
      if (Math.hypot(dx, dz) > 2) { this.err = { x: 0, z: 0 }; }   // a teleport (respawn, round start): snap
      else { const k = Math.max(0, 1 - (recvMs - this.errAt) / T.PRED_SMOOTH_MS); this.err = { x: this.err.x * k + dx, z: this.err.z * k + dz }; this.errAt = recvMs; }
    }
    this.pred = p;
  }
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;
function lerpAngle(a: number, b: number, f: number) { let d = b - a; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI; return a + d * f; }
